import React, { useState, useEffect } from 'react';
import CompanyFormSettingsService, { CompanyFormConfiguration, FormRequirementRule } from '../../../services/CompanyFormSettingsService';
import FormTemplateService, { FormTemplateDefinition } from '../../../services/FormTemplateService';
import { useConfirm } from '../../../hooks/useConfirm';

const CompanyFormSettings: React.FC = () => {
  const formSettingsService = CompanyFormSettingsService.getInstance();
  const formTemplateService = FormTemplateService.getInstance();
  const { confirm, ConfirmDialog } = useConfirm();

  const DEFAULT_COMPANY_ID = 'default-company';

  const [companySettings, setCompanySettings] = useState<CompanyFormConfiguration | null>(
    formSettingsService.getCompanySettings(DEFAULT_COMPANY_ID)
  );
  const [formRequirements, setFormRequirements] = useState<FormRequirementRule[]>([]);
  const [blockBooking, setBlockBooking] = useState<boolean>(
    companySettings?.blockBookingUntilComplete ?? true
  );
  const [showSaveSuccessModal, setShowSaveSuccessModal] = useState(false);

  useEffect(() => {
    loadFormRequirements();

    const updateSettings = () => {
      const newSettings = formSettingsService.getCompanySettings(DEFAULT_COMPANY_ID);
      setCompanySettings(newSettings);
      setBlockBooking(newSettings?.blockBookingUntilComplete ?? true);
      loadFormRequirements();
    };

    formSettingsService.subscribe(updateSettings);
    return () => formSettingsService.unsubscribe(updateSettings);
  }, []);

  const loadFormRequirements = async () => {
    const requirements = await formSettingsService.getFormRequirements(DEFAULT_COMPANY_ID);
    setFormRequirements(requirements);
  };

  const handleToggleRequired = async (formTemplateId: string, currentlyRequired: boolean) => {
    if (currentlyRequired) {
      // Remove from required list
      await formSettingsService.removeAutoAssignForm(DEFAULT_COMPANY_ID, formTemplateId, 'admin');
    } else {
      // Add to required list
      await formSettingsService.addAutoAssignForm(DEFAULT_COMPANY_ID, formTemplateId, 'admin');
    }
    loadFormRequirements();
  };

  const handleToggleOptional = async (formTemplateId: string, currentlyOptional: boolean) => {
    const settings = formSettingsService.getCompanySettings(DEFAULT_COMPANY_ID);
    if (!settings) return;

    if (currentlyOptional) {
      // Remove from optional list
      const updatedOptional = settings.optionalForms.filter(id => id !== formTemplateId);
      await formSettingsService.updateCompanySettings(
        DEFAULT_COMPANY_ID,
        { optionalForms: updatedOptional },
        'admin'
      );
    } else {
      // Add to optional list
      const updatedOptional = Array.from(new Set([...settings.optionalForms, formTemplateId]));
      await formSettingsService.updateCompanySettings(
        DEFAULT_COMPANY_ID,
        { optionalForms: updatedOptional },
        'admin'
      );
    }
    loadFormRequirements();
  };

  const handleToggleBlockBooking = async () => {
    const newValue = !blockBooking;
    setBlockBooking(newValue);
    await formSettingsService.setBookingBlock(DEFAULT_COMPANY_ID, newValue, 'admin');
  };

  const handleSave = () => {
    setShowSaveSuccessModal(true);
  };

  const handleResetToDefaults = async () => {
    if (await confirm({ title: 'Reset Form Settings', message: 'Reset to default form settings? This will set the standard required forms for new clients.', confirmLabel: 'Reset', variant: 'warning' })) {
      await formSettingsService.resetToDefaults(DEFAULT_COMPANY_ID, 'admin');
      loadFormRequirements();
    }
  };

  const summary = formSettingsService.getSettingsSummary(DEFAULT_COMPANY_ID);

  return (
    <div style={{ padding: '32px', maxWidth: '1200px' }}>
      {/* Save Button - Top Right */}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '24px' }}>
        <button
          onClick={handleResetToDefaults}
          style={{
            padding: '10px 24px',
            backgroundColor: 'transparent',
            color: '#FF6B6B',
            border: '1px solid #FF6B6B',
            borderRadius: '6px',
            fontSize: '14px',
            fontWeight: 600,
            cursor: 'pointer'
          }}
        >
          Reset to defaults
        </button>
        <button
          onClick={handleSave}
          style={{
            padding: '10px 24px',
            backgroundColor: '#226F9E',
            color: '#FFFFFF',
            border: 'none',
            borderRadius: '6px',
            fontSize: '14px',
            fontWeight: 600,
            cursor: 'pointer'
          }}
        >
          Save changes
        </button>
      </div>

      {/* Page Header */}
      <div style={{ marginBottom: '32px' }}>
        <h1 style={{ fontSize: '28px', fontWeight: 700, color: '#FFFFFF', margin: '0 0 8px 0' }}>
          New Client Form Requirements
        </h1>
        <p style={{ fontSize: '14px', color: '#87CEEB', margin: 0 }}>
          Configure which forms are automatically assigned to new clients when they sign up
        </p>
      </div>

      {/* Summary Card */}
      {summary && (
        <div style={{
          backgroundColor: '#113F67',
          border: '1px solid #6CA6C1',
          borderRadius: '8px',
          padding: '24px',
          marginBottom: '32px'
        }}>
          <h3 style={{ fontSize: '16px', fontWeight: 600, color: '#F7E9A0', marginBottom: '16px' }}>
            Current Configuration
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '24px' }}>
            <div>
              <div style={{ fontSize: '12px', color: '#87CEEB', marginBottom: '4px' }}>Required Forms</div>
              <div style={{ fontSize: '24px', fontWeight: 700, color: '#FFFFFF' }}>{summary.requiredFormsCount}</div>
            </div>
            <div>
              <div style={{ fontSize: '12px', color: '#87CEEB', marginBottom: '4px' }}>Optional Forms</div>
              <div style={{ fontSize: '24px', fontWeight: 700, color: '#FFFFFF' }}>{summary.optionalFormsCount}</div>
            </div>
            <div>
              <div style={{ fontSize: '12px', color: '#87CEEB', marginBottom: '4px' }}>Booking Restriction</div>
              <div style={{ fontSize: '16px', fontWeight: 600, color: summary.blockingEnabled ? '#4CAF50' : '#FF6B6B' }}>
                {summary.blockingEnabled ? 'Enabled' : 'Disabled'}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Appointment Booking Block Setting */}
      <div style={{
        backgroundColor: '#113F67',
        border: '1px solid #6CA6C1',
        borderRadius: '8px',
        padding: '24px',
        marginBottom: '32px'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div style={{ flex: 1 }}>
            <h3 style={{ fontSize: '16px', fontWeight: 600, color: '#FFFFFF', marginBottom: '8px' }}>
              Block Appointment Booking
            </h3>
            <p style={{ fontSize: '14px', color: '#87CEEB', margin: 0 }}>
              Prevent clients from booking appointments until all required forms are completed. This ensures you have all necessary intake information before the first session.
            </p>
          </div>
          <label style={{
            position: 'relative',
            display: 'inline-block',
            width: '60px',
            height: '34px',
            marginLeft: '24px',
            flexShrink: 0
          }}>
            <input
              type="checkbox"
              checked={blockBooking}
              onChange={handleToggleBlockBooking}
              style={{ opacity: 0, width: 0, height: 0 }}
            />
            <span style={{
              position: 'absolute',
              cursor: 'pointer',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: blockBooking ? '#4CAF50' : '#6CA6C1',
              transition: '0.4s',
              borderRadius: '34px'
            }}>
              <span style={{
                position: 'absolute',
                content: '',
                height: '26px',
                width: '26px',
                left: blockBooking ? '30px' : '4px',
                bottom: '4px',
                backgroundColor: 'white',
                transition: '0.4s',
                borderRadius: '50%'
              }} />
            </span>
          </label>
        </div>
      </div>

      {/* Form Requirements Table */}
      <div style={{ marginBottom: '32px' }}>
        <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#FFFFFF', marginBottom: '16px' }}>
          Form Assignment Settings
        </h3>
        <p style={{ fontSize: '14px', color: '#87CEEB', marginBottom: '16px' }}>
          Select which forms should be automatically assigned to new clients. Required forms must be completed before clients can book appointments (if blocking is enabled). Optional forms are recommended but not mandatory.
        </p>

        <div style={{
          backgroundColor: '#113F67',
          border: '1px solid #6CA6C1',
          borderRadius: '8px',
          overflow: 'hidden'
        }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#0A2540' }}>
                <th style={{
                  padding: '16px',
                  textAlign: 'left',
                  color: '#F7E9A0',
                  fontSize: '12px',
                  fontWeight: 600,
                  letterSpacing: '0.05em'
                }}>
                  FORM NAME
                </th>
                <th style={{
                  padding: '16px',
                  textAlign: 'left',
                  color: '#F7E9A0',
                  fontSize: '12px',
                  fontWeight: 600,
                  letterSpacing: '0.05em'
                }}>
                  CATEGORY
                </th>
                <th style={{
                  padding: '16px',
                  textAlign: 'center',
                  color: '#F7E9A0',
                  fontSize: '12px',
                  fontWeight: 600,
                  letterSpacing: '0.05em'
                }}>
                  REQUIRED
                </th>
                <th style={{
                  padding: '16px',
                  textAlign: 'center',
                  color: '#F7E9A0',
                  fontSize: '12px',
                  fontWeight: 600,
                  letterSpacing: '0.05em'
                }}>
                  OPTIONAL
                </th>
              </tr>
            </thead>
            <tbody>
              {formRequirements.length === 0 ? (
                <tr>
                  <td colSpan={4} style={{
                    padding: '32px',
                    textAlign: 'center',
                    color: '#87CEEB',
                    fontSize: '14px'
                  }}>
                    No forms available. Create form templates first.
                  </td>
                </tr>
              ) : (
                formRequirements.map((form, index) => {
                  const isOptional = companySettings?.optionalForms.includes(form.formTemplateId) ?? false;

                  return (
                    <tr
                      key={form.formTemplateId}
                      style={{
                        borderTop: index > 0 ? '1px solid #28527A' : 'none',
                        backgroundColor: form.isRequired ? 'rgba(76, 175, 80, 0.1)' : 'transparent'
                      }}
                    >
                      <td style={{ padding: '16px', color: '#FFFFFF', fontSize: '14px' }}>
                        <div style={{ fontWeight: form.isRequired ? 600 : 400 }}>
                          {form.formName}
                        </div>
                      </td>
                      <td style={{ padding: '16px', color: '#87CEEB', fontSize: '14px' }}>
                        <span style={{
                          display: 'inline-block',
                          padding: '4px 12px',
                          backgroundColor: '#0A2540',
                          borderRadius: '12px',
                          fontSize: '12px',
                          textTransform: 'capitalize'
                        }}>
                          {form.category}
                        </span>
                      </td>
                      <td style={{ padding: '16px', textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          checked={form.isRequired}
                          onChange={() => handleToggleRequired(form.formTemplateId, form.isRequired)}
                          style={{
                            width: '20px',
                            height: '20px',
                            cursor: 'pointer',
                            accentColor: '#4CAF50'
                          }}
                        />
                      </td>
                      <td style={{ padding: '16px', textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          checked={isOptional}
                          onChange={() => handleToggleOptional(form.formTemplateId, isOptional)}
                          disabled={form.isRequired} // Can't be both required and optional
                          style={{
                            width: '20px',
                            height: '20px',
                            cursor: form.isRequired ? 'not-allowed' : 'pointer',
                            accentColor: '#6CA6C1',
                            opacity: form.isRequired ? 0.3 : 1
                          }}
                        />
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Help Text */}
      <div style={{
        backgroundColor: '#113F67',
        border: '1px solid #6CA6C1',
        borderRadius: '8px',
        padding: '20px',
        marginBottom: '32px'
      }}>
        <h4 style={{ fontSize: '14px', fontWeight: 600, color: '#F7E9A0', marginBottom: '12px' }}>
          How Form Assignment Works
        </h4>
        <ul style={{
          margin: 0,
          paddingLeft: '20px',
          color: '#87CEEB',
          fontSize: '14px',
          lineHeight: '1.8'
        }}>
          <li><strong style={{ color: '#FFFFFF' }}>Required Forms:</strong> Automatically assigned to new clients when they sign up. Must be completed before booking appointments (if blocking is enabled).</li>
          <li><strong style={{ color: '#FFFFFF' }}>Optional Forms:</strong> Assigned to new clients but not required for appointment booking. Clients can complete these at their convenience.</li>
          <li><strong style={{ color: '#FFFFFF' }}>Unassigned Forms:</strong> Not automatically assigned but can be manually assigned to specific clients by providers.</li>
        </ul>
      </div>

      {/* Save Success Modal */}
      {showSaveSuccessModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: '#0A2540',
            border: '1px solid #6CA6C1',
            borderRadius: '12px',
            padding: '32px',
            width: '400px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>✓</div>
            <h2 style={{
              color: '#FFFFFF',
              marginBottom: '16px',
              fontSize: '18px',
              fontWeight: 600
            }}>
              Form settings saved successfully!
            </h2>
            <p style={{
              color: '#87CEEB',
              fontSize: '14px',
              marginBottom: '24px'
            }}>
              New clients will now receive the selected forms upon signup.
            </p>
            <button
              onClick={() => setShowSaveSuccessModal(false)}
              style={{
                padding: '10px 32px',
                backgroundColor: '#226F9E',
                color: '#FFFFFF',
                border: 'none',
                borderRadius: '24px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 600
              }}
            >
              OK
            </button>
          </div>
        </div>
      )}
      {ConfirmDialog}
    </div>
  );
};

export default CompanyFormSettings;
